import React, { useMemo } from "react";
import { get, noop, findIndex } from "lodash";
import { TernaryFn } from "@tvg/ts-types/Functional";
import {
  RaceCardBettingInterest,
  RaceOdds,
  RaceTypeCodeEnum
} from "@tvg/ts-types/Race";
import tvgConf from "@tvg/conf";
import MagnetScrollArea from "@tvg/atomic-ui/_templates/MagnetScrollArea";
import { Saddle } from "@tvg/design-system/web";
import {
  HorseContainer,
  HorseDescription,
  RunnerNumberContainer,
  HorseSwitcherWrapper,
  RunnerName,
  ScrollAreaDisabled
} from "./styled-components";

export const formatOdds = (currentOdd: RaceOdds) =>
  get(currentOdd, "denominator")
    ? `${get(currentOdd, "numerator", 0)}/${get(currentOdd, "denominator")}`
    : get(currentOdd, "numerator", 0);

const renderRunners = (
  runners: RaceCardBettingInterest[],
  onRunnerClick: TernaryFn<string, string, number, void>,
  activeRunner: string
) => {
  const isIOSApp =
    tvgConf().product === "ios2" || tvgConf().product === "iosnative";

  return runners.map((bi, index) =>
    bi.runners.map((runner) => (
      <HorseContainer
        isActive={runner.horseName === activeRunner}
        onClick={() =>
          onRunnerClick(
            runner.horseName,
            get(runner, "entityRunnerId", ""),
            index
          )
        }
      >
        <RunnerNumberContainer>
          <Saddle
            size="s"
            saddleColor={bi?.saddleColor}
            numberColor={bi?.numberColor}
            number={runner.runnerId}
            isScratched={runner.scratched}
            qaLabel={`saddle-container-${runner.runnerId}`}
          />
        </RunnerNumberContainer>
        <HorseDescription
          isScratched={runner.scratched}
          isActive={runner.horseName === activeRunner}
          isIOSApp={isIOSApp}
        >
          <RunnerName isScratched={runner.scratched}>
            {runner.horseName}
          </RunnerName>
          <span>{` - ${
            runner.scratched ? "SCR" : formatOdds(bi.currentOdds)
          }`}</span>
        </HorseDescription>
      </HorseContainer>
    ))
  );
};

export const findActiveRunner = (
  runners: RaceCardBettingInterest[],
  activeRunner: string
) =>
  findIndex(runners, (runner) => runner.runners[0].horseName === activeRunner);

type Props = {
  runners: RaceCardBettingInterest[];
  onRunnerClick: TernaryFn<string, string, number, void>;
  activeRunner: string;
  raceTypeCode: RaceTypeCodeEnum;
  isDisabled: boolean;
};

const HorseSwitcher = (props: Props) => {
  const { runners, onRunnerClick, activeRunner, isDisabled } = props;

  return useMemo(
    () =>
      runners.length > 0 ? (
        <HorseSwitcherWrapper>
          {isDisabled ? (
            <ScrollAreaDisabled>
              {renderRunners(runners, onRunnerClick, activeRunner)}
            </ScrollAreaDisabled>
          ) : (
            <MagnetScrollArea
              isLoading={false}
              isFreeScroll
              isRunnerScroll
              activeIndex={findActiveRunner(runners, activeRunner)}
            >
              {renderRunners(runners, onRunnerClick, activeRunner)}
            </MagnetScrollArea>
          )}
        </HorseSwitcherWrapper>
      ) : null,
    [JSON.stringify(runners), activeRunner, isDisabled]
  );
};

HorseSwitcher.defaultProps = {
  runners: [],
  onRunnerClick: noop,
  activeRunner: "",
  raceTypeCode: "T",
  isDisabled: false
};

export default HorseSwitcher;
